import { useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { TaskType, useTrackerTasksQuery } from '../../../queries/useTrackerTasksQuery';
import { Box, Typography } from '@mui/material';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';
import { compact } from 'lodash';
import { formatDate } from 'utility/helpers';
import { Link } from 'react-router-dom';
import { USER_PROFILE } from 'constants/routes';
import { useTrackerContext } from '../../../context';
import { useFormContext } from 'react-hook-form';
import { TaskFormData } from '../../../hooks/useAddTaskForm';
import { getCustomFieldsDefaultValues, useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { useDynamicTaskColumns } from '../../../hooks';
import CalendarLineIcon from 'remixicon-react/CalendarLineIcon';
import { theme } from 'config/theme';
import Avatar from 'components/common/avatar/Avatar';
import { useTrackerDetailsQuery } from '@buildwise/libs/trackers/queries';

function TasksList() {
  const {
    state: { trackerId, wrapText, selectedTask, spanRows, filteredTasks },
    dispatch,
  } = useTrackerContext();
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const { isLoading, userMap } = useTrackerTasksQuery({
    trackerId,
    page: 0,
    size: 1000,
    shouldFetch: false,
  });
  const { companyId } = useUserData();
  const { tracker } = useTrackerDetailsQuery({
    trackerId,
    shouldFetch: false,
  });
  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: tracker?.configNameForInputFields,
  });
  const dynamicColumns = useDynamicTaskColumns(activeUniqueEntries);
  const columns = useMemo(() => {
    const columns: GridColDef<TaskType>[] = compact([
      { field: 'id', headerName: 'ID', width: 30, disableColumnMenu: true },
      ...dynamicColumns,
      {
        field: 'createdBy',
        headerName: 'Created By',
        width: 200,
        renderCell: ({ row }) => (
          <Link to={USER_PROFILE.replace(':userName', row?.createdBy || '')}>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              {userMap[row.createdBy] && (
                <Avatar
                  src={userMap[row.createdBy]?.[0]?.profileImageURL}
                  size={26}
                  fontSize={14}
                  name={userMap[row.createdBy]?.[0]?.displayName || row.createdBy}
                />
              )}{' '}
              <Typography
                sx={(theme) => ({
                  color: theme.palette.custom.tuna,
                  fontSize: 15,
                  fontWeight: 500,
                })}
              >
                {userMap[row.createdBy]?.[0]?.displayName || row.createdBy}
              </Typography>
            </Box>
          </Link>
        ),
        rowSpanValueGetter: () => null,
      },
      {
        field: 'createdAt',
        headerName: 'Created On',
        width: 170,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarLineIcon size={18} color={theme.palette.custom.tuna} />
            <Typography
              sx={(theme) => ({
                color: theme.palette.custom.tuna,
                fontSize: 15,
                fontWeight: 500,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              })}
            >
              {formatDate(row.createdAt)}
            </Typography>
          </Box>
        ),
      },
    ]);
    return columns;
  }, [dynamicColumns]);

  const getTableHeight = () => {
    if (tableContainerRef.current) {
      const containerTop = tableContainerRef.current.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - containerTop;
      return availableHeight > 100 ? availableHeight - 8 : 100;
    }
    return undefined;
  };

  useLayoutEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.style.height = `${getTableHeight()}px`;
      tableContainerRef.current.style.minHeight = `${getTableHeight()}px`;
    }
  }, []);

  useEffect(() => {
    const updateHeight = () => {
      if (tableContainerRef.current) {
        const height = getTableHeight();
        if (height) {
          tableContainerRef.current.style.height = `${height}px`;
          tableContainerRef.current.style.minHeight = `${height}px`;
        }
      }
    };

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredTasks]);

  return (
    <Box
      ref={tableContainerRef}
      sx={{
        transition: 'height 0.1s',
        mt: 2,
        backgroundColor: theme.palette.custom.white,
      }}
    >
      <DataGrid
        loading={isLoading}
        columns={columns}
        rows={filteredTasks}
        checkboxSelection={false}
        hideFooter={filteredTasks.length <= 100}
        columnHeaderHeight={44}
        columnVisibilityModel={{ id: false }}
        {...(spanRows && {
          unstable_rowSpanning: true,
        })}
        showCellVerticalBorder={true}
        showColumnVerticalBorder={true}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          dispatch(({ state }) => ({
            payload: {
              ...state,
              selectedTask: row,
            },
          }));
        }}
        onCellKeyDown={(params, event) => {
          let task: TaskType | undefined;

          if (event.code === 'ArrowDown') {
            const taskIndex = filteredTasks.findIndex((task) => task.id === params.row.id);
            if (taskIndex > -1) {
              task = filteredTasks[taskIndex + 1];
            } else {
              task = filteredTasks[0];
            }
          }

          if (event.code === 'ArrowUp') {
            const taskIndex = filteredTasks.findIndex((task) => task.id === params.row.id);
            if (taskIndex > -1) {
              task = filteredTasks[taskIndex - 1];
            }
          }

          if (task) {
            dispatch(({ state }) => ({
              payload: {
                ...state,
                selectedTask: task,
              },
            }));
          }
        }}
        rowSelectionModel={selectedTask?.id ? [selectedTask?.id] : []}
        getRowHeight={() => (wrapText ? 'auto' : undefined)}
        sx={(theme) => ({
          [`& .${gridClasses.cell}`]: {
            padding: '8px 16px',
            color: theme.palette.custom.tuna,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            color: theme.palette.custom.tuna,
          },
          '& .MuiDataGrid-columnHeader': {
            padding: '8px 16px',
            backgroundColor: theme.palette.custom.blueChalk,
            color: theme.palette.custom.tuna,
          },
          '& .MuiDataGrid-row--dynamicHeight': {
            '& [data-colindex]': {
              border: `1px solid ${theme.palette.custom.whiteSmoke}`,
            },
          },
        })}
      />
    </Box>
  );
}

export default TasksList;
